import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {BroadcastsService} from "../services/broadcasts.service";
import {Observable, Subscription, timer} from "rxjs";
import {Game} from "../model/broadcast/game";
import {AuthService} from "../services/auth.service";
import {AbstractControl, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule} from "@angular/forms";
import {Broadcast} from "../model/broadcast/broadcast";
import {ActivatedRoute} from "@angular/router";
import {ConfigDialogComponent} from "./config-dialog/config-dialog.component";
import {Control, ControlComponent} from "../model/broadcast/control";
import {LowerThirdDialogComponent} from "./lower-third-dialog/lower-third-dialog.component";
import {CustomTableDialogComponent} from "./custom-table-dialog/custom-table-dialog.component";
import {ManualLineupsDialogComponent} from "./manual-lineups-dialog/manual-lineups-dialog.component";
import {RosterDialogComponent} from "./roster-dialog/roster-dialog.component";
import {GamesService} from "../services/games.service";
import {BroadcastGamePair} from "../model/broadcast-game-pair";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatDividerModule} from "@angular/material/divider";
import {ClipboardModule} from "@angular/cdk/clipboard";
import {NgIf, NgFor, NgClass, AsyncPipe, DatePipe} from "@angular/common";
import {importProvidersFrom} from "@angular/core";
import {BroadcastLayerModule} from "../broadcast-layer-module/broadcast-layer.module";
import {StatusComponent} from "../broadcast-layer-module/broadcast-layer/status/status.component";
import {BatterComponent} from "../broadcast-layer-module/broadcast-layer/batter/batter.component";
import {Player} from "../model/broadcast/player";
import {UtilService} from "../services/util.service";
import {MatGridListModule} from "@angular/material/grid-list";
import {Team} from "../model/broadcast/team";
import {League} from "../model/league";
import {LeaguesService} from "../services/leagues.service";
import {take} from "rxjs/operators";

const AVAILABLE_COMPONENTS: ControlComponent[] = [
  'intro', 'status', 'boxScore', 'awayLineup', 'homeDefense', 'homeLineup', 'awayDefense', 'batter', 'pitcher',
  'lowerThird', 'customTable',
  'tipsportRti', 'staticPartner'
  //, 'hrDerby', 'hrDerbyStandings'
];

@Component({
    selector: 'app-broadcast-setup',
    templateUrl: './broadcast-setup.component.html',
    styleUrls: ['./broadcast-setup.component.scss'],
    standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    ClipboardModule,
    NgIf,
    NgFor,
    NgClass,
    AsyncPipe,
    DatePipe,
    StatusComponent,
    BroadcastLayerModule,
    BatterComponent,
    MatGridListModule
  ]
})
export class BroadcastSetupComponent implements OnInit, OnDestroy {
  // Properties to track key sequence
  private keySequence: string = '';
  private keySequenceTimer: any = null;
  private readonly KEY_SEQUENCE_TIMEOUT = 1500; // 1 second timeout for key sequence

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (this.dialog.openDialogs.length) {
      return;
    }
    if(event.code === "KeyS") {
      this.toggleComponent('status');
    } else if(event.code === "KeyB") {
      this.toggleComponent('batter');
    } else if(event.code === "KeyT") {
      this.toggleComponent('lowerThird');
    } else if(event.code === "KeyO") {
      this.toggleComponent('boxScore');
    } else if(event.code === "KeyD") {
      this.toggleComponent(event.shiftKey ? 'homeDefense' : 'awayDefense');
    }  else if(event.code === "KeyL") {
      this.toggleComponent(event.shiftKey ? 'homeLineup' : 'awayLineup');
    } else if((event.code.startsWith('Digit') || event.code.startsWith('Numpad')) && this.keySequence === '') {
      // Only handle direct number selection if there's no active key sequence
      let number = Number(event.code.replace('Digit', '').replace('Numpad', ''));
      //let team = event.shiftKey ? this.game.home : this.game.away;
      let lineup = this.getCurrentLineup(!event.shiftKey);
      console.log(event);
      if (number === 0) {
        this.selectPerson(lineup[9]);
      } else {
        this.selectPerson(lineup[number - 1]);
      }
    } else {
      // Handle the new key sequence pattern
      this.handleKeySequence(event);
    }
  }

  broadcast: Broadcast;
  league: League;
  controls$: Observable<Control[]>
  broadcast$: Observable<Broadcast>;
  game: Game;
  game$: Observable<Game>;
  gameSub: Subscription;
  subscriptions: Subscription[] = [];
  uid: string;
  public controlsFormData: UntypedFormGroup;
  private controlButtonSubscriptions: Subscription[] = [];
  isLinkedToCurrentUser: boolean = false;
  constructor(
    private authService: AuthService,
    private broadcastService: BroadcastsService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private gamesService: GamesService,
    private leaguesService: LeaguesService,
    public utilService: UtilService
  ) {
    this.uid = this.route.snapshot.paramMap.get('uid');
  }

  // CSS třída skinu pro náhled (komponenty zde nejsou obalené app-broadcast-layer,
  // takže třídu skinu musíme nasadit na wrapper náhledu ručně).
  get skinCssClass(): string {
    return this.utilService.getSkinDef(this.league?.skinId, this.league?.url).cssClass;
  }

  // Zděděný skin z ligy zapíšeme do config (in-memory), aby skin-aware util metody
  // (barvy / skrytí log / kód kraje) fungovaly i v náhledu setupu.
  private applySkin(): void {
    if (this.broadcast?.config) {
      this.broadcast.config.skinId = this.utilService.getSkinDef(this.league?.skinId, this.league?.url).id;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => {s.unsubscribe();});
    this.controlButtonSubscriptions.forEach(s => {s.unsubscribe();});
  }

  ngOnInit(): void {
    // take(1): init jen jednou, až je auth k dispozici. Bez něj se celý blok
    // re-spustí při každém re-emitu user$ (změna users/{uid} docu, refresh
    // tokenu) a nastacká duplicitní controls$/broadcast$ subscriptiony i
    // opakované createControlForm → zbytečné překreslování lišty.
    this.authService.user$.pipe(take(1)).subscribe(user => {
      this.controls$ = this.broadcastService.getControls(this.uid);
      const controlsSubs = this.controls$.subscribe(controls => {
        this.createControlForm(controls);
      });
      this.subscriptions.push(controlsSubs);
      this.broadcast$ = this.broadcastService.getBroadcast(this.uid);
      const broadcastSubscription: Subscription = this.broadcast$.subscribe(broadcast => {
        this.broadcast = broadcast;
        if (broadcast?.leagueId && this.league?.id != broadcast.leagueId) {
          this.leaguesService.getLeague(broadcast.leagueId).pipe(take(1)).subscribe(l => {
            this.league = l;
            this.applySkin();
          });
        }
        this.applySkin();
        if (this.gameSub) {
          this.gameSub.unsubscribe();
        }
        if (broadcast?.gameId) {
          this.game$ = this.gamesService.getGame(broadcast.gameId);
          this.gameSub = this.game$.subscribe(game => {
            this.game = game;
          });
        }
      });
      this.subscriptions.push(broadcastSubscription);

      // Check if the broadcast is already linked to the current user
      const linkedBroadcastSub = this.broadcastService.getLinkedBroadcastUid().subscribe(linkedBroadcastUid => {
        this.isLinkedToCurrentUser = linkedBroadcastUid === this.uid;
      });
      this.subscriptions.push(linkedBroadcastSub);
    });
  }

  private createControlForm(controls: Control[]): void {
    this.controlButtonSubscriptions.forEach(s => {
      s.unsubscribe();
    });
    this.controlsFormData = new UntypedFormGroup({});
    this.controlsFormData.addControl('broadcastUrl', new UntypedFormControl(''));
    this.controlsFormData.addControl('selectedPersonName', new UntypedFormControl(this.broadcast?.manualData?.selectedPerson?.name));
    controls.forEach((control) => {
      const formControl = new UntypedFormControl(
        this.broadcastService.getControlAction(controls, control.component), [Validators.required]
      );
      this.controlsFormData.addControl(control.component as string, formControl);
      const sub = formControl.valueChanges.subscribe(newValue => {
        this.broadcastService.updateControl(this.uid, {
          action: newValue, component: control.component, index: control.index
        });
      });
      this.controlButtonSubscriptions.push(sub);
    });
    this.getBroadcastUrl();
  }

  openConfigDialog(): void {
    const dialogRef = this.dialog.open(ConfigDialogComponent, {
      width: '720px',
      data: this.broadcast,
    });
  }

  openLowerThirdDialog(): void {
    const data: BroadcastGamePair = {game: this.game, broadcast: this.broadcast};
    const dialogRef = this.dialog.open(LowerThirdDialogComponent, {
      width: '860px',
      data,
    });
  }

  openCustomTableDialog(): void {
    const dialogRef = this.dialog.open(CustomTableDialogComponent, {
      width: '860px',
      data: {broadcast: this.broadcast, game: this.game}
    });
  }

  openManualLineupsDialog(): void {
    const dialogRef = this.dialog.open(ManualLineupsDialogComponent, {
      width: '860px',
      data: this.broadcast
    });
  }

  openRosterDialog(): void {
    const dialogRef = this.dialog.open(RosterDialogComponent, {
      width: '860px',
      data: this.broadcast
    });
  }

  tabActive(tab: ControlComponent): boolean {
    return this.controlsFormData?.get(tab as string)?.value === 'on';
  }

  toggleComponent(controlTab: ControlComponent): void {
    this.toggleControl({component: controlTab});
  }

  // trackBy podle stabilního klíče (= component, zároveň id control-docu).
  // Bez něj *ngFor nad Firestore collectionData (nové instance objektů při
  // každém snapshotu) zboří a znovu vykreslí všechny dlaždice při každém
  // zápisu controlu → na čerstvých elementech se přehraje CSS transition a
  // celá lišta problikne. S trackBy Angular DOM uzly recykluje a aktualizuje
  // jen změněnou dlaždici.
  trackByComponent(_index: number, control: Control): string {
    return control.component as string;
  }

  toggleControl(controlTab: Control): void {
    if(controlTab.component === 'poweredBy') return;
    let control: AbstractControl = this.controlsFormData.get(controlTab.component);
    control.setValue(control.value === 'on' ? 'off' : 'on');

    if (controlTab.component === 'tipsportRti' && control?.value != 'off') {
      console.log('showing');
      const rtiTimer: Subscription = timer(10000).subscribe(time => {
        console.log('hiding');
        this.broadcastService.updateControl(this.broadcast.uid, {action: 'off', component: controlTab.component, index: controlTab.index});
        rtiTimer.unsubscribe();
      });
    }

    if (controlTab.component === 'partnerLogos' && control?.value != 'off') {
      console.log('showing');
      const rtiTimer: Subscription = timer(10250).subscribe(time => {
        console.log('hiding');
        this.broadcastService.updateControl(this.broadcast.uid, {action: 'off', component: controlTab.component, index: controlTab.index});
        rtiTimer.unsubscribe();
      });
    }
  }

  getComponentIcon(component: string): string {
    switch (component) {
      case 'intro': return 'star';
      case 'status': return 'scoreboard';
      case 'relatedGames': return 'scoreboard';
      case 'batter': return 'sports_cricket';
      case 'boxScore': return 'pin';
      case 'homeLineup': return 'people_outline';
      case 'awayLineup': return 'people';
      case 'homeDefense': return 'people_outline';
      case 'awayDefense': return 'people';
      case 'pitcher': return 'sports_handball';
      default: return '';
    }
  }

  getComponentTitle(component: string): string {
    switch (component) {
      case 'intro': return 'Intro';
      case 'status': return 'Status';
      case 'relatedGames': return 'Related games';
      case 'batter': return 'Current batter';
      case 'boxScore': return 'Box score';
      case 'homeLineup': return 'Home lineup';
      case 'awayLineup': return 'Away lineup';
      case 'homeDefense': return 'Home defense';
      case 'awayDefense': return 'Away defense';
      case 'pitcher': return 'Current pitcher';
      case 'staticPartner': return 'Tipsport logo';
      case 'tipsportRti': return 'Tipsport RTI';
      case 'lowerThird': return 'Lower third';
      case 'customTable': return 'Custom table';
      case 'partnerLogos': return 'Partner logos';
      default: return '';
    }
  }

  getBroadcastUrl(): string {
    return window.location.href.replace('broadcast', 'broadcast-layer');
  }

  getControlUrl(): string {
    return window.location.href.replace('broadcast', 'broadcast-control');
  }

  getScoresheetUrl(): string {
    return window.location.href.replace('broadcast', 'score-sheet');
  }

  selectPerson(person: Player): void {
    if (!person) {
      return;
    }

    // If using manual lineups and game data is available, try to find player stats in game data
    if (this.broadcast?.config?.useManualLineups && this.game && person) {
      // Check if the player is from a manual lineup by checking if it's in the manual lineup arrays
      const isFromManualLineup =
        (this.broadcast.manualLineups?.homeLineup && this.broadcast.manualLineups.homeLineup.some(p => p.guid === person.guid)) ||
        (this.broadcast.manualLineups?.awayLineup && this.broadcast.manualLineups.awayLineup.some(p => p.guid === person.guid)) ||
        (this.broadcast.manualLineups?.homePitcher && this.broadcast.manualLineups.homePitcher.guid === person.guid) ||
        (this.broadcast.manualLineups?.awayPitcher && this.broadcast.manualLineups.awayPitcher.guid === person.guid);

      if (isFromManualLineup) {
        // Determine which team the player is on
        const isHomeTeam = person.teamGuid === this.broadcast.config.homeConfig.id;
        const team = isHomeTeam ? this.game.home : this.game.away;

        if (team) {
          // Look for the player in the team's lineup
          let gamePlayer: Player = null;

          // Check lineup
          if (team.lineup) {
            for (const spot of team.lineup) {
              for (const player of spot.players) {
                if (player.guid === person.guid) {
                  gamePlayer = player;
                  break;
                }
              }
              if (gamePlayer) break;
            }
          }

          // Check pitchers if not found in lineup
          if (!gamePlayer && team.pitchers) {
            gamePlayer = team.pitchers.find(p => p.guid === person.guid);
          }

          // If player found in game data, copy stats to the manual lineup player
          if (gamePlayer) {
            const enhancedPlayer = { ...person };
            if (gamePlayer.gameStats) enhancedPlayer.gameStats = gamePlayer.gameStats;
            if (gamePlayer.seasonStats) enhancedPlayer.seasonStats = gamePlayer.seasonStats;
            if (gamePlayer.records) enhancedPlayer.records = gamePlayer.records;

            this.broadcast.lowerThird = enhancedPlayer;
            this.broadcastService.updateLowerThird(this.broadcast);
            return;
          }
        }
      }
    }

    // Default behavior if not using manual lineups or player not found in game data
    this.broadcast.lowerThird = person;
    this.broadcastService.updateLowerThird(this.broadcast);
  }

  /**
   * Links the current broadcast to the user
   */
  linkBroadcast(): void {
    this.broadcastService.linkBroadcastToUserOutput(this.uid).then(() => {
      this.isLinkedToCurrentUser = true;
    });
  }

  getCurrentLineup(away: boolean): Player[] {
    // If useManualLineups is enabled and manual lineup data exists, use it
    if (this.broadcast?.config?.useManualLineups && this.broadcast?.manualLineups) {
      if (!away && this.broadcast.manualLineups.homeLineup?.length > 0) {
        const lineup = [...this.broadcast.manualLineups.homeLineup];
        if (this.broadcast.manualLineups.homePitcher) {
          lineup.push(this.broadcast.manualLineups.homePitcher);
        }
        return lineup;
      } else if (away && this.broadcast.manualLineups.awayLineup?.length > 0) {
        const lineup = [...this.broadcast.manualLineups.awayLineup];
        if (this.broadcast.manualLineups.awayPitcher) {
          lineup.push(this.broadcast.manualLineups.awayPitcher);
        }
        return lineup;
      }
    }

    // Fall back to game data if manual lineup is not available or not enabled
    const team = away ? this.game?.away : this.game?.home;
    if (!team?.lineup) return [];
    const lineup: Player[] = team.lineup.map(s => s.players[s.players.length - 1]);
    lineup.push(team.pitchers[team.pitchers.length - 1]);
    return lineup;
  }

  forceGameUpdate(): void {
    this.gamesService.forceGameUpdate(this.broadcast.gameId, this.broadcast.leagueId);
  }

  /**
   * Handles key sequence input for player selection
   * @param event The keyboard event
   */
  private handleKeySequence(event: KeyboardEvent): void {
    // Reset the timer if it exists
    if (this.keySequenceTimer) {
      clearTimeout(this.keySequenceTimer);
    }

    // Add the key to the sequence if it's a letter or number
    if (/^[a-zA-Z0-9]$/.test(event.key)) {
      this.keySequence += event.key.toLowerCase();

      // Set a timer to reset the sequence after the timeout
      this.keySequenceTimer = setTimeout(() => {
        this.keySequence = '';
      }, this.KEY_SEQUENCE_TIMEOUT);

      // Check if the sequence matches our pattern
      this.processKeySequence();
    } else {
      // Reset the sequence for other keys
      this.keySequence = '';
    }
  }

  /**
   * Processes the current key sequence to check for player selection patterns
   */
  private processKeySequence(): void {
    // Check for home team pattern: h followed by numbers
    if (this.keySequence.match(/^h\d{2}$/)) {
      const playerNumber = parseInt(this.keySequence.substring(1), 10);
      this.selectPlayerByNumber(playerNumber, false); // false = home team
      this.keySequence = ''; // Reset sequence after processing
    }
    // Check for away team pattern: a followed by numbers
    else if (this.keySequence.match(/^a\d{2}$/)) {
      const playerNumber = parseInt(this.keySequence.substring(1), 10);
      this.selectPlayerByNumber(playerNumber, true); // true = away team
      this.keySequence = ''; // Reset sequence after processing
    }
  }

  /**
   * Selects a player by their number from the specified team roster
   * @param playerNumber The player's number to search for
   * @param away Whether to search in the away team (true) or home team (false)
   */
  private selectPlayerByNumber(playerNumber: number, away: boolean): void {

    const teamRoster: Player[] = this.broadcast.fullRosters.filter(p => p.teamGuid == (away ? this.broadcast.config.awayConfig.id : this.broadcast.config.homeConfig.id) && p.number !== undefined);
    // Find the player with the matching number
    const player: Player =  teamRoster.find(p => p.number === playerNumber);
    if (player) {
      this.selectPerson(player);
      console.log(`Selected ${away ? 'away' : 'home'} player #${playerNumber}: ${player.name}`);
    } else {
      console.log(`No ${away ? 'away' : 'home'} player found with number ${playerNumber}`);
    }
  }

  isAdmin(): Boolean {
    return this.authService.user?.role === 'admin';
  }
}
